import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { PositionDetail } from "../../api/structure";
import { MapPicker } from "../../components/MapPicker";
import { AssetSummaryCard } from "../../components/AssetSummaryCard";
import Ic from "../../components/icons.jsx";
import { formatDateTime } from "../../lib/dates";
import { canDismountAll, dangerBtn, formatLocation } from "./shared";

/** Read-only banner on a soft-deleted card: who, when, why, and "Vrati" with `structure:delete`. */
export function DeletedBanner({ detail, canRestore, onRestore }: { detail: PositionDetail; canRestore: boolean; onRestore: () => void }) {
  const { t } = useTranslation();
  if (!detail.deletedAt) return null;
  return (
    <div className="deleted-banner" role="status">
      <Ic.alert aria-hidden="true" />
      <div className="deleted-banner-text">
        <div className="deleted-banner-title">{t("structure.deletedBanner.title", "Obrisana pozicija — samo za čitanje")}</div>
        <div>
          {t("structure.deletedBanner.who", "Obrisao: {{who}} · {{when}}", { who: detail.deletedByName ?? t("structure.deletedBanner.unknown", "nepoznato"), when: formatDateTime(detail.deletedAt) })}
        </div>
        <div><b>{t("structure.deletedBanner.reason", "Razlog:")}</b> {detail.deletedReason ?? "—"}</div>
      </div>
      {canRestore && (
        <button type="button" className="btn primary" onClick={onRestore}>
          {t("structure.restore", "Vrati")}
        </button>
      )}
    </div>
  );
}

/** Physical location (own or inherited from an ancestor) with the map when it has GPS. */
export function LocationSection({ detail }: { detail: PositionDetail }) {
  const { t } = useTranslation();
  return (
    <section className="card">
      <div className="card-h"><h3>{t("structure.section.location", "Lokacija")}</h3></div>
      <div className="card-b">
        <dl className="kv">
          <dt>{t("structure.field.location", "Fizička lokacija")}</dt>
          <dd>
            {detail.effectiveLocation ? (
              <>
                {formatLocation(detail.effectiveLocation)}
                {!detail.locationId && (
                  <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>{t("structure.locationInheritedHint", "(nasleđena od pretka)")}</span>
                )}
              </>
            ) : <span className="muted">{t("structure.locationNone", "Nije zadata ni na jednom pretku.")}</span>}
          </dd>
        </dl>
        {detail.effectiveLocation?.lat != null && detail.effectiveLocation?.lng != null && (
          <div style={{ marginTop: 12 }}>
            <MapPicker key={detail.effectiveLocation.id} lat={detail.effectiveLocation.lat} lng={detail.effectiveLocation.lng} readOnly height={220} />
          </div>
        )}
      </div>
    </section>
  );
}

/** Drawing code, note and the intervention note — hidden when all three are empty. */
export function NotesSection({ detail }: { detail: PositionDetail }) {
  const { t } = useTranslation();
  if (!detail.drawingCode && !detail.note && !detail.interventionNote) return null;
  return (
    <section className="card">
      <div className="card-h"><h3>{t("structure.section.notes", "Šifra crteža / napomene")}</h3></div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <dl className="kv">
          <dt>{t("structure.field.drawingCode", "Šifra crteža")}</dt>
          <dd className="mono">{detail.drawingCode ?? <span className="muted">—</span>}</dd>
          <dt>{t("structure.field.note", "Napomena")}</dt>
          <dd style={{ whiteSpace: "pre-wrap" }}>{detail.note || <span className="muted">—</span>}</dd>
        </dl>
        {detail.interventionNote && (
          <div className="note-intervention" role="note">
            <div className="src"><Ic.alert /> {t("structure.field.interventionNote", "Napomena za intervenciju")}</div>
            <div style={{ whiteSpace: "pre-wrap" }}>{detail.interventionNote}</div>
          </div>
        )}
      </div>
    </section>
  );
}

/** The asset mounted here (dismount / open), the empty slot (mount), or a structural node's note. */
export function MountedAssetSection({
  detail, holdsAsset, canMount, canViewAssets, onMount, onDismount, onDismountAll,
}: {
  detail: PositionDetail;
  holdsAsset: boolean;
  canMount: boolean;
  canViewAssets: boolean;
  onMount: () => void;
  onDismount: () => void;
  onDismountAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="card">
      <div className="card-h">
        <h3>{t("structure.section.mounted", "Montiran asset")}</h3>
        {canMount && canDismountAll(detail) && (
          <button type="button" className="btn sm" style={dangerBtn} onClick={onDismountAll}>
            {t("structure.dismountAll", "Demontiraj ceo sklop")}
          </button>
        )}
      </div>
      {detail.mountedAsset ? (
        <div className="card-b">
          <AssetSummaryCard
            asset={detail.mountedAsset}
            showPosition={false}
            actions={(
              <>
                {canMount && (
                  <button type="button" className="btn primary lg" onClick={onDismount}>
                    {t("assets.action.dismount", "Demontiraj")}
                  </button>
                )}
                {canViewAssets && (
                  <Link to={`/asseti/${detail.mountedAsset.id}`} className="btn lg" style={{ textDecoration: "none" }}>
                    {t("structure.openKarton", "Otvori karton")} <Ic.arrowRight />
                  </Link>
                )}
              </>
            )}
          />
        </div>
      ) : !holdsAsset ? (
        <div className="empty">{t("structure.mountedStructural", "Strukturni čvor — asseti se montiraju na pod-pozicije.")}</div>
      ) : (
        <div className="empty" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div>
            {detail.mountState === "EMPTY"
              ? t("structure.mountedEmptyExpected", "Prazna pozicija — očekuje se {{name}}.", { name: detail.expectedTypeName ?? "" })
              : t("structure.mountedEmpty", "Prazna pozicija.")}
          </div>
          {canMount && (
            <button type="button" className="btn primary lg" onClick={onMount}>
              <Ic.plus /> {t("structure.mountAsset", "Montiraj asset")}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
